/**
 * Payroll KPI Dashboard tab — split out of pages/Payroll.jsx (2026-02).
 *
 * Super-user-only trend view: margin/session, cost/session, % of
 * revenue, contractor mix — every metric a rolling series of past
 * finalized runs. Includes the tiny trendline() helper. Zero
 * behaviour change from the pre-split inline definitions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { fmtMonYY } from '../../lib/date';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { BarChart3, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { C, usd, usdShort, pct, int } from '../../lib/payrollHelpers';
import { MiniKpi } from './ArchiveTab';

function trendline(values) {
  const n = values.length;
  if (n < 2) return values.map(() => null);
  const xs = values.map((_, i) => i);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = values.reduce((a, b) => a + b, 0) / n;
  const num = xs.reduce((acc, x, i) => acc + (x - meanX) * (values[i] - meanY), 0);
  const den = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0) || 1;
  const slope = num / den;
  const intercept = meanY - slope * meanX;
  return xs.map(x => slope * x + intercept);
}

function KpiDashboard() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/payroll/kpi/history').then(({ data }) => setHistory(data.history || []))
      .catch(() => toast.error('Could not load KPI history'))
      .finally(() => setLoading(false));
  }, []);
  const chart = useMemo(() => {
    // Denominator = show + LC + NS. Prefer the per-unit fields (which are
    // already total_units-based); fall back to legacy show-unit fields for
    // very old snapshots.
    const base = (history || []).map(h => ({
      period: (h.period_end || '').slice(5),
      income_per_session: (h.income_per_unit_cents || h.income_per_show_unit_cents || 0) / 100,
      payroll_per_session: (h.payroll_per_unit_cents || h.payroll_per_show_unit_cents || 0) / 100,
      margin_per_session: ((h.income_per_unit_cents || h.income_per_show_unit_cents || 0)
                          - (h.payroll_per_unit_cents || h.payroll_per_show_unit_cents || 0)) / 100,
      margin_pct: (h.margin_pct || 0) * 100,
      payroll_pct_of_revenue: h.income_cents ? (h.payroll_cents / h.income_cents) * 100 : 0,
      lc_ns_rate_pct: (h.lc_ns_rate_pct || 0) * 100,
    }));
    // Attach linear-regression trendlines for each series so bar charts
    // still convey trajectory without cluttering the legend.
    const keys = ['income_per_session', 'payroll_per_session', 'margin_per_session',
                  'margin_pct', 'payroll_pct_of_revenue', 'lc_ns_rate_pct'];
    const trends = Object.fromEntries(keys.map(k => [k, trendline(base.map(b => b[k]))]));
    return base.map((row, i) => ({
      ...row,
      income_per_session_trend: trends.income_per_session[i],
      payroll_per_session_trend: trends.payroll_per_session[i],
      margin_per_session_trend: trends.margin_per_session[i],
      margin_pct_trend: trends.margin_pct[i],
      payroll_pct_trend: trends.payroll_pct_of_revenue[i],
      lc_ns_rate_pct_trend: trends.lc_ns_rate_pct[i],
    }));
  }, [history]);
  if (loading) return <div className="p-8 text-center" style={{ color: C.soft }}>Loading KPIs…</div>;
  if (!history.length) return <Card className="p-6 border text-center" style={{ borderColor: C.border, color: C.soft }}>No finalized periods yet.</Card>;

  const last6 = chart.slice(-6), prior6 = chart.slice(-12, -6);
  const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  const marginNow = avg(last6.map(x => x.margin_pct));
  const marginPrior = avg(prior6.map(x => x.margin_pct));
  const best = chart.reduce((a, b) => (b.margin_per_session > a.margin_per_session ? b : a), chart[0]);
  const worst = chart.reduce((a, b) => (b.margin_per_session < a.margin_per_session ? b : a), chart[0]);

  return (
    <div className="space-y-4" data-testid="pr-kpi-dashboard">
      <div className="grid md:grid-cols-4 gap-3">
        <MiniKpi label="Last-6 avg Margin %" v={`${marginNow.toFixed(1)}%`} />
        <MiniKpi label="Prior-6 avg Margin %" v={`${marginPrior.toFixed(1)}%`} />
        <MiniKpi label="Best margin/session" v={`$${best?.margin_per_session.toFixed(2)} · ${best?.period}`} />
        <MiniKpi label="Worst margin/session" v={`$${worst?.margin_per_session.toFixed(2)} · ${worst?.period}`} />
      </div>
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="pr-kpi-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>
          Income / Payroll / Margin per session
          <span className="ml-2 font-normal normal-case tracking-normal" style={{ color: C.soft }}>
            · denominator = Show + LC + NS
          </span>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
            <XAxis dataKey="period" fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} tickFormatter={v => `$${v.toFixed(0)}`} />
            <Tooltip formatter={v => (v == null ? '—' : `$${Number(v).toFixed(2)}`)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="income_per_session"  name="Income / Session"  fill={C.plum} radius={[4,4,0,0]} maxBarSize={22} />
            <Bar dataKey="payroll_per_session" name="Payroll / Session" fill={C.rose} radius={[4,4,0,0]} maxBarSize={22} />
            <Bar dataKey="margin_per_session"  name="Margin / Session"  fill={C.ok}   radius={[4,4,0,0]} maxBarSize={22} />
            <Line type="linear" dataKey="income_per_session_trend"  name="Income trend"  stroke={C.plum} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" />
            <Line type="linear" dataKey="payroll_per_session_trend" name="Payroll trend" stroke={C.rose} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" />
            <Line type="linear" dataKey="margin_per_session_trend"  name="Margin trend"  stroke={C.ok}   strokeDasharray="4 3" strokeWidth={2}   dot={false} legendType="none" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="pr-kpi-margin-pct pr-kpi-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Payroll % of revenue · Margin %</div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
            <XAxis dataKey="period" fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} tickFormatter={v => `${v.toFixed(0)}%`} />
            <Tooltip formatter={v => (v == null ? '—' : `${Number(v).toFixed(1)}%`)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="payroll_pct_of_revenue" name="Payroll % Revenue" fill={C.rose} radius={[4,4,0,0]} maxBarSize={26} />
            <Bar dataKey="margin_pct"             name="Margin %"          fill={C.ok}   radius={[4,4,0,0]} maxBarSize={26} />
            <Line type="linear" dataKey="payroll_pct_trend" name="Payroll trend" stroke={C.rose} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" />
            <Line type="linear" dataKey="margin_pct_trend"  name="Margin trend"  stroke={C.ok}   strokeDasharray="4 3" strokeWidth={2}   dot={false} legendType="none" />
            <ReferenceLine y={0} stroke="#000" strokeOpacity={0.15} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="pr-kpi-lcns pr-kpi-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>LC/NS rate trend</div>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
            <XAxis dataKey="period" fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} tickFormatter={v => `${v.toFixed(1)}%`} />
            <Tooltip formatter={v => (v == null ? '—' : `${Number(v).toFixed(2)}%`)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="lc_ns_rate_pct" name="LC/NS %" fill={C.amber} radius={[4,4,0,0]} maxBarSize={26} />
            <Line type="linear" dataKey="lc_ns_rate_pct_trend" name="LC/NS trend" stroke={C.amber} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}



export { KpiDashboard };
